import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowDownTrayIcon,
  DocumentIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import tprmService from '../../../services/compliance/tprm.service';
import { useComplianceAccess } from '../../../hooks/useComplianceAccess';
import type { VendorDocument } from '../../../types/compliance';
import Notification, { NotificationContainer } from '../../../components/Notification';
import { saveAs } from '../../../utils/saveFile';
import { useUserNames } from '../../../hooks/useUserNames';


interface Props {
  vendorId: string;
}


function formatSize(bytes: number): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}



/** Files tab on VendorDetail — shows ALL vendor documents, both
 *  evidence-attached and standalone. Evidence-attached documents get a
 *  small marker so reviewers know where they came from. */
export default function VendorFilesPanel({ vendorId }: Props) {
  const { nameFor } = useUserNames();
  const { isManager: canDelete } = useComplianceAccess();

  const [docs, setDocs] = useState<VendorDocument[]>([]);
  const [evidenceDocIds, setEvidenceDocIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [err, setErr] = useState<{ title: string; message: string } | null>(null);
  const [scope, setScope] = useState<'all' | 'evidence' | 'other'>('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Load docs + evidence in parallel so we can label evidence-attached
      // documents without a backend change. Evidence.document_id is the link.
      const [docRows, evidenceRows] = await Promise.all([
        tprmService.listVendorDocuments(vendorId),
        tprmService.listEvidence(vendorId),
      ]);
      setDocs(docRows);
      setEvidenceDocIds(
        new Set(
          evidenceRows
            .map((e) => e.document_id)
            .filter((id): id is string => Boolean(id)),
        ),
      );
    } catch (error: any) {
      const msg =
        error?.response?.data?.detail || error?.message || 'Unknown error';
      setErr({ title: 'Failed to load files', message: msg });
    } finally {
      setLoading(false);
    }
  }, [vendorId]);

  useEffect(() => { load(); }, [load]);

  const isEvidence = useCallback(
    (d: VendorDocument) => evidenceDocIds.has(d.id),
    [evidenceDocIds],
  );

  const filtered = useMemo(() => {
    if (scope === 'all') return docs;
    return scope === 'evidence'
      ? docs.filter(isEvidence)
      : docs.filter((d) => !isEvidence(d));
  }, [docs, scope, isEvidence]);

  const download = useCallback(async (doc: VendorDocument) => {
    setBusyId(doc.id);
    try {
      await saveAs(doc.original_filename, () =>
        tprmService.downloadVendorDocument(vendorId, doc.id),
      );
    } catch (error: any) {
      const msg =
        error?.response?.data?.detail || error?.message || 'Unknown error';
      setErr({ title: 'Download failed', message: msg });
    } finally {
      setBusyId(null);
    }
  }, [vendorId]);

  const remove = useCallback(async (doc: VendorDocument) => {
    if (!window.confirm(`Delete "${doc.original_filename}"?`)) return;
    setBusyId(doc.id);
    try {
      await tprmService.deleteVendorDocument(vendorId, doc.id);
      setDocs((prev) => prev.filter((d) => d.id !== doc.id));
    } catch (error: any) {
      const msg =
        error?.response?.data?.detail || error?.message || 'Unknown error';
      setErr({ title: 'Delete failed', message: msg });
    } finally {
      setBusyId(null);
    }
  }, [vendorId]);

  return (
    <div className="space-y-3">
      <NotificationContainer>
        {err && (
          <Notification
            type="error"
            title={err.title}
            message={err.message}
            onClose={() => setErr(null)}
          />
        )}
      </NotificationContainer>

      <div className="flex items-center justify-between gap-3">
        <div className="text-sm text-gray-600 dark:text-gray-300">
          {loading
            ? 'Loading…'
            : `${filtered.length} of ${docs.length} file${docs.length === 1 ? '' : 's'}`}
        </div>
        <div className="flex gap-1 text-xs">
          {(['all', 'evidence', 'other'] as const).map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => setScope(opt)}
              className={`px-2 py-1 rounded border ${
                scope === opt
                  ? 'border-blue-600 bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                  : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-highlight'
              }`}
            >
              {opt === 'all' ? 'All' : opt === 'evidence' ? 'Evidence-attached' : 'Other'}
            </button>
          ))}
        </div>
      </div>

      {!loading && filtered.length === 0 && (
        <div className="text-sm text-muted">
          No files match this filter.
        </div>
      )}

      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-panel">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-muted border-b border-gray-200 dark:border-gray-700">
              <th className="py-2 px-3">File</th>
              <th className="py-2 px-3">Size</th>
              <th className="py-2 px-3">Uploaded</th>
              <th className="py-2 px-3">Uploaded by</th>
              <th className="py-2 px-3 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((d) => (
              <tr
                key={d.id}
                className="border-b border-gray-100 dark:border-gray-800"
              >
                <td className="py-2 px-3">
                  <div className="flex items-center gap-2">
                    <DocumentIcon className="w-4 h-4 text-gray-400" />
                    <button
                      type="button"
                      onClick={() => download(d)}
                      disabled={busyId === d.id}
                      className="text-blue-600 dark:text-blue-400 hover:underline text-left disabled:opacity-50"
                      title={d.original_filename}
                    >
                      {d.original_filename}
                    </button>
                    {isEvidence(d) && (
                      <span className="text-[0.625rem] font-medium px-1.5 py-0.5 rounded bg-warn text-warn-fg">
                        evidence
                      </span>
                    )}
                  </div>
                  {d.description && (
                    <div className="text-xs text-muted mt-0.5">
                      {d.description}
                    </div>
                  )}
                </td>
                <td className="py-2 px-3 tabular-nums text-gray-700 dark:text-gray-200">
                  {formatSize(d.file_size)}
                </td>
                <td className="py-2 px-3 text-gray-500">
                  {d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString() : '—'}
                </td>
                <td className="py-2 px-3 text-gray-500 truncate max-w-[200px]">
                  {nameFor(d.uploaded_by_oid) || '—'}
                </td>
                <td className="py-2 px-3 text-right">
                  <div className="inline-flex gap-1">
                    <button
                      type="button"
                      onClick={() => download(d)}
                      disabled={busyId === d.id}
                      className="p-1 text-muted hover:text-gray-700 disabled:opacity-50"
                      title="Download"
                    >
                      <ArrowDownTrayIcon className="w-4 h-4" />
                    </button>
                    {canDelete && (
                      <button
                        type="button"
                        onClick={() => remove(d)}
                        disabled={busyId === d.id}
                        className="p-1 text-red-600 hover:text-red-800 dark:text-red-400 disabled:opacity-50"
                        title="Delete"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
